import React from 'react';
import { BookOpen, CheckCircle, XCircle, AlertTriangle, Calendar, Info } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────
// MANUAL CONTABLE PARA CORREDORES — Riesgo Unido
// Guía en lenguaje sencillo. Basada en la normativa panameña (DGI, SSRP, CSS)
// verificada en junio 2026. NO sustituye el criterio de un contador idóneo.
// ──────────────────────────────────────────────────────────────────────────

const Card = ({ children, style }) => (
  <div className="card" style={{ marginBottom: 16, ...style }}>{children}</div>
);
const H = ({ icon: Icon, children, color = 'var(--sky)' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
    {Icon && <Icon size={20} style={{ color }} />}
    <div style={{ fontSize: 18, fontWeight: 800 }}>{children}</div>
  </div>
);
const Li = ({ children }) => (
  <li style={{ marginBottom: 8, lineHeight: 1.55, color: '#cdd9e5' }}>{children}</li>
);

export default function ManualContable() {
  return (
    <div style={{ maxWidth: 920 }}>
      <Card style={{ background: 'linear-gradient(135deg, rgba(var(--sky-rgb),0.15), rgba(var(--steel-rgb),0.4))' }}>
        <H icon={BookOpen}>Manual contable para corredores de seguros</H>
        <p style={{ color: '#cdd9e5', lineHeight: 1.6 }}>
          Esta guía explica, en palabras simples, cómo llevar la contabilidad de Riesgo Unido
          cumpliendo la ley panameña. Está escrita para que <strong>no necesites ser contador</strong> para
          entenderla. Aun así, las decisiones con impacto fiscal deben confirmarse con un
          <strong> contador idóneo (CPA)</strong>. Datos verificados a junio 2026.
        </p>
      </Card>

      {/* LA REGLA DE ORO */}
      <Card style={{ borderColor: 'rgba(245, 158, 11, 0.4)' }}>
        <H icon={AlertTriangle} color="#F59E0B">La regla de oro: la prima NO es tu ingreso</H>
        <p style={{ color: '#cdd9e5', lineHeight: 1.6, marginBottom: 12 }}>
          Cuando un cliente te paga la prima del seguro, ese dinero <strong>no es una venta tuya</strong>:
          es dinero que pasa por tus manos camino a la aseguradora (dinero en tránsito). Tu
          <strong> único ingreso real es la COMISIÓN</strong> que la aseguradora te paga por colocar la póliza.
        </p>
        <ul style={{ paddingLeft: 20 }}>
          <Li><strong>Si contaras la prima como ingreso</strong>, inflarías tus ventas y pagarías muchísimo
            impuesto de más. Es el error #1 de los corredores pequeños.</Li>
          <Li>El sistema ya separa esto: los <strong>cobros</strong> (primas) van por su lado y las
            <strong> comisiones</strong> son tu ingreso. El módulo de contabilidad solo toma las comisiones.</Li>
          <Li>Lo ideal es tener una <strong>cuenta bancaria aparte</strong> para las primas de clientes,
            distinta de la cuenta de tu operación.</Li>
        </ul>
      </Card>

      {/* QUÉ SÍ Y QUÉ NO REGISTRAR */}
      <div className="grid-2" style={{ marginBottom: 16 }}>
        <div className="card" style={{ borderColor: 'rgba(34, 197, 94, 0.35)' }}>
          <H icon={CheckCircle} color="#22C55E">Qué SÍ registrar</H>
          <ul style={{ paddingLeft: 20 }}>
            <Li><strong>Comisiones cobradas</strong> de aseguradoras (tu ingreso real). Se cargan solas
              desde el módulo de Comisiones.</Li>
            <Li><strong>Gastos del negocio</strong>: alquiler, internet, software, honorarios del contador,
              publicidad, papelería, transporte, comisiones bancarias.</Li>
            <Li>El <strong>ITBMS que pagas</strong> en tus compras (por si algún día te inscribes y lo
              puedes acreditar).</Li>
            <Li>Los <strong>impuestos y tasas</strong> que pagas (renta, SSRP, CSS).</Li>
            <Li>Guarda <strong>siempre la factura/recibo</strong> de cada gasto (puedes adjuntarla).</Li>
          </ul>
        </div>
        <div className="card" style={{ borderColor: 'rgba(239, 68, 68, 0.35)' }}>
          <H icon={XCircle} color="#EF4444">Qué NO registrar como ingreso/gasto</H>
          <ul style={{ paddingLeft: 20 }}>
            <Li><strong>La prima del cliente</strong> NO es ingreso (es dinero en tránsito a la aseguradora).</Li>
            <Li><strong>Las comisiones que pagas a agentes/referidos</strong> NO las metas a mano: ya salen
              automáticas de la operación y están descontadas de tu comisión neta.</Li>
            <Li><strong>Gastos personales</strong> (tuyos, no del negocio) no van aquí. Mezclarlos es un
              riesgo en una auditoría.</Li>
            <Li>El dinero que solo <strong>recaudas y entregas</strong> (no es tuyo) no es ni ingreso ni gasto.</Li>
          </ul>
        </div>
      </div>

      {/* TUS IMPUESTOS COMO PERSONA NATURAL */}
      <Card>
        <H icon={Info}>Tus impuestos hoy (Persona Natural)</H>
        <p style={{ color: '#cdd9e5', lineHeight: 1.6, marginBottom: 12 }}>
          Como corredor <strong>persona natural</strong>, tu carga es más liviana que la de una sociedad:
        </p>
        <ul style={{ paddingLeft: 20 }}>
          <Li><strong>Impuesto sobre la Renta (ISR):</strong> tabla progresiva. Los primeros
            <strong> $11,000 de ganancia están exentos</strong>; de $11,000 a $50,000 pagas 15%; sobre lo que
            pase de $50,000, 25%. Se declara hasta el <strong>15 de marzo</strong>.</Li>
          <Li><strong>Tasa de regulación de la Superintendencia (SSRP):</strong> 0.25% de tus comisiones del
            año anterior (mínimo $100). Se paga hasta el <strong>31 de marzo</strong>.</Li>
          <Li><strong>Fianza ante la SSRP:</strong> debes mantenerla vigente (mínimo $10,000 de cobertura).</Li>
          <Li><strong>CSS como independiente:</strong> con la reforma de 2025 es obligatorio cotizar
            (IVM 9.36%). Aún no estás inscrito — está marcado como recomendación pendiente.</Li>
          <Li><strong>ITBMS:</strong> solo aplica si facturas más de $36,000 al año en comisiones y te
            inscribes. Hoy <strong>no estás inscrito</strong>. (Ver la nota del recuadro de abajo.)</Li>
          <Li><strong>NO pagas</strong> Tasa Única de S.A. ($300) ni Aviso de Operación — esos son de las
            sociedades. Aparecerán solos el día que pases a persona jurídica.</Li>
        </ul>
      </Card>

      {/* PUNTO AMBIGUO ITBMS */}
      <Card style={{ borderColor: 'rgba(245, 158, 11, 0.4)' }}>
        <H icon={AlertTriangle} color="#F59E0B">Punto a confirmar con tu contador: ITBMS sobre comisiones</H>
        <p style={{ color: '#cdd9e5', lineHeight: 1.6 }}>
          La prima de seguro está <strong>exenta</strong> de ITBMS. Lo que no está 100% claro en la ley es si
          la <strong>comisión del corredor</strong> (un servicio de intermediación) lleva el 7% de ITBMS o no.
          Hay dos lecturas válidas y es una decisión de mucho dinero. Por eso el sistema lo deja como un
          <strong> interruptor apagado por defecto</strong> (asumiendo exento). Antes de cambiarlo, conviene una
          consulta formal a la DGI o el criterio escrito de tu contador. Mientras tanto, si una situación
          así aparece, anótala en la pestaña <strong>“Recomendaciones y consultas”</strong>.
        </p>
      </Card>

      {/* CALENDARIO */}
      <Card>
        <H icon={Calendar}>Calendario tributario (lo que aplica hoy)</H>
        <div className="table-wrapper" style={{ maxHeight: 'none' }}>
          <table>
            <thead><tr><th>Mes</th><th>Obligación</th><th>Entidad</th></tr></thead>
            <tbody>
              <tr><td>Mensual</td><td>Cuota CSS independiente (cuando te inscribas)</td><td>CSS</td></tr>
              <tr><td>15 marzo</td><td>Declaración de Renta (ISR)</td><td>DGI</td></tr>
              <tr><td>31 marzo</td><td>Tasa de regulación 0.25% comisiones</td><td>SSRP</td></tr>
              <tr><td>30 junio</td><td>Impuesto estimado — 1ª partida</td><td>DGI</td></tr>
              <tr><td>30 sept.</td><td>Impuesto estimado — 2ª partida</td><td>DGI</td></tr>
              <tr><td>31 dic.</td><td>Impuesto estimado — 3ª partida</td><td>DGI</td></tr>
              <tr><td>Anual</td><td>Mantener fianza SSRP vigente</td><td>SSRP</td></tr>
            </tbody>
          </table>
        </div>
        <p style={{ color: 'var(--silver)', fontSize: 13, marginTop: 10 }}>
          La pestaña <strong>“Obligaciones”</strong> lleva este calendario vivo: te muestra lo que vence pronto
          y puedes marcar cada pago como realizado.
        </p>
      </Card>

      {/* ERRORES QUE MULTAN */}
      <Card style={{ borderColor: 'rgba(239, 68, 68, 0.35)' }}>
        <H icon={AlertTriangle} color="#EF4444">Errores que te pueden multar (evítalos)</H>
        <ul style={{ paddingLeft: 20 }}>
          <Li>Contar la prima como ingreso (infla renta e impuestos).</Li>
          <Li>Mezclar el dinero de primas de clientes con tu dinero operativo.</Li>
          <Li>No declarar la renta a tiempo (15 marzo) o no pagar el impuesto estimado (jun/sep/dic).</Li>
          <Li>Dejar vencer la tasa de regulación o la fianza de la Superintendencia.</Li>
          <Li>No guardar las facturas de tus gastos (mínimo 5 años) — sin soporte no son deducibles.</Li>
          <Li>Tratar a un agente como empleado de hecho (horario, subordinación) sin tenerlo en planilla:
            la CSS puede recalificarlo y cobrarte cuotas retroactivas.</Li>
        </ul>
      </Card>

      <p style={{ color: 'var(--silver)', fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
        Este manual es una guía operativa de Riesgo Unido y no constituye asesoría legal ni fiscal formal.
        Las cifras y plazos pueden cambiar; confírmalos con tu contador idóneo y con las entidades (DGI,
        SSRP, CSS) antes de cada pago.
      </p>
    </div>
  );
}
